/**
 * The persona contract: how the platform READS pinned agent content.
 *
 * Extracted from `deploy-prompt-advisor.ts` so the deploy and the contract
 * verifier derive an agent's toolset from ONE function. Before this, only the
 * deploy composed tools from a persona; the verifier compared a deployed agent
 * against a hand-maintained copy of the tool definitions that lived in the
 * platform. Once the persona became the sole author, that copy was answering the
 * wrong question — "does this agent match the PLATFORM's idea?" instead of "does
 * it match its PIN?" — and nothing in CI could catch the two drifting, because
 * persona content is hydrated from a pin and CI does not hydrate.
 *
 * A second derivation of an agent's TOOLSET is therefore not a style problem: it
 * is the drift. (Scope, stated precisely: `deploy-prompt-advisor` and this module
 * are the only readers of `targets.foundry.*` tools. Other persona frontmatter
 * fields — `persona-a2a.ts`'s a2a card, for one — are still parsed elsewhere;
 * that is a separate question this module does not claim to have settled.)
 */
import * as fs from "node:fs";
import * as path from "node:path";
import { parse as parseYaml } from "yaml";

import { LocalCliError } from "./errors.js";
import { agentContentHint } from "./agent-content-sync.js";

/**
 * Markers delimiting the decision policy inside a persona body.
 *
 * The persona is their ONLY author. A platform script used to hold its own copy
 * of the policy and, on every run, strip whatever the persona supplied and
 * substitute that copy — so the deployed wording was whichever author ran last.
 * Nothing in the platform may write between these markers; they exist here so a
 * verifier can CHECK that a deployed agent carries a policy. Reading is not
 * authoring.
 */
export const DECISION_POLICY_START = "<!-- m8t:decision-policy:start -->";
export const DECISION_POLICY_END = "<!-- m8t:decision-policy:end -->";

export interface PersonaFrontmatter {
  version?: string | number;
  targets?: {
    foundry?: {
      model?: string;
      reasoning?: { effort?: string };
      /** Forwarded to Foundry VERBATIM — see `personaFoundryTools`. */
      tools?: unknown;
    };
  };
  "fillable-fields"?: { name: string; default?: string }[];
}

export interface LoadedPersona {
  /** The file the frontmatter was read from — for error copy and re-rendering. */
  readonly personaPath: string;
  readonly frontmatter: PersonaFrontmatter;
}

/**
 * Walk up from `hint` until a directory containing `rel` is found.
 *
 * Callers pass a checkout root that may be any subdirectory of one (the
 * documented verifier invocations run from `apps/cli`), so anything resolved
 * relative to it must search the same way `findPersonasRoot` does. Resolving one
 * input by walking up and another by direct join is how a command works for the
 * author and fails for everyone else.
 */
export function findAncestorContaining(hint: string, rel: string): string | null {
  let dir = path.resolve(hint);
  const fsRoot = path.parse(dir).root;
  for (;;) {
    if (fs.existsSync(path.join(dir, rel))) return dir;
    const parent = path.dirname(dir);
    if (parent === dir || dir === fsRoot) return null;
    dir = parent;
  }
}

/**
 * Resolve the personas directory root starting from `hint`, walking up until a
 * `personas/` directory is found. This allows callers to pass `process.cwd()`
 * even when the cwd is a subdirectory of the repo checkout (e.g. `apps/cli/`).
 *
 * Returns `null` when neither `hint` nor any ancestor contains a `personas/` dir.
 */
export function findPersonasRoot(hint: string): string | null {
  return findAncestorContaining(hint, "personas");
}

/** Where `<persona>`'s file lives, given a checkout root or any subdirectory of one. */
function personaPathFor(repoRoot: string, persona: string): string {
  const resolvedRoot = findPersonasRoot(repoRoot);
  return resolvedRoot
    ? path.join(resolvedRoot, "personas", persona, "persona.md")
    : path.join(repoRoot, "personas", persona, "persona.md");
}

/**
 * Read and parse a persona's frontmatter, refusing LOUDLY on absent or empty
 * content.
 *
 * The empty arm is the one that matters and the one that is easy to omit. A
 * zero-byte or frontmatter-less persona parses to `{}`, which declares no tools
 * — indistinguishable, downstream, from an agent that legitimately has none. A
 * verifier handed that would compare a deployed agent against an empty
 * expectation and report agreement. "Found nothing, so nothing is wrong" is how
 * a gate certifies the exact state it exists to catch.
 */
export function loadPersonaFrontmatter(repoRoot: string, persona: string): LoadedPersona {
  const resolvedRoot = findPersonasRoot(repoRoot);
  const personaPath = personaPathFor(repoRoot, persona);

  let raw: string;
  try {
    raw = fs.readFileSync(personaPath, "utf8");
  } catch (e) {
    throw new LocalCliError({
      code: "ADVISOR_PERSONA_MISSING",
      message: `Could not read the persona at '${personaPath}'.`,
      // A product agent's persona is generated from its pinned repo rather than
      // committed here, so an un-hydrated checkout is the likeliest cause and
      // "run from your checkout" would send the reader the wrong way.
      hint:
        agentContentHint(resolvedRoot ?? repoRoot, persona) ??
        "Run from your m8t repo checkout, or pass --repo-root.",
      cause: e,
    });
  }

  // \r?\n tolerates CRLF line endings (Windows git checkouts) as well as LF.
  const fmMatch = /^---\r?\n([\s\S]*?)\r?\n---/.exec(raw);
  if (!fmMatch) {
    throw new LocalCliError({
      code: "ADVISOR_PERSONA_EMPTY",
      message: `The persona at '${personaPath}' has no frontmatter block — it is empty or truncated.`,
      hint:
        agentContentHint(resolvedRoot ?? repoRoot, persona) ??
        "Re-materialize the persona; a persona with no frontmatter declares nothing.",
    });
  }

  return { personaPath, frontmatter: (parseYaml(fmMatch[1]) ?? {}) as PersonaFrontmatter };
}

/**
 * The persona's declared Foundry tools, forwarded VERBATIM.
 *
 * The persona file is the declaration (documented in docs/architecture/SCHEMA.md);
 * this is only the courier, so a new Foundry tool type needs no change here.
 *
 * This used to be dropped on the floor: the frontmatter declared tools, nothing
 * read them, and a freshly installed advisor came up with only what `brain link`
 * and `a2a enable` append afterwards. The Ezra persona's grounding rule instructs
 * it to "call the Microsoft Learn MCP first" — a tool the deploy never granted. A
 * capability the instructions BELIEVE IN but the deploy withholds is worse than an
 * absent one: the agent reports it cannot find a tool it was told it has.
 *
 * Fails closed on a malformed declaration rather than deploying a silently
 * tool-less agent, which is the failure mode that hid this for so long.
 */
export function personaFoundryTools(
  persona: string,
  frontmatter: PersonaFrontmatter,
): Record<string, unknown>[] | undefined {
  const rawTools = frontmatter.targets?.foundry?.tools;
  if (rawTools === undefined) return undefined;
  if (!Array.isArray(rawTools)) {
    throw new LocalCliError({
      code: "ADVISOR_BAD_TOOLS",
      message: `targets.foundry.tools in persona '${persona}' must be a list, got ${typeof rawTools}.`,
      hint: `Fix the tools block in personas/${persona}/persona.md.`,
    });
  }
  // Array-ness alone was not enough. `tools: ["present_decision"]` — a list of
  // NAMES, the shape a human reaches for first — passed, was forwarded verbatim
  // to Foundry, and could not be checked afterwards either, because a bare string
  // has no identity to match a deployed tool against. Every entry must at least be
  // an object carrying a string `type`; beyond that this stays a courier, so a new
  // Foundry tool type still needs no change here.
  rawTools.forEach((entry, index) => {
    const type = (entry as { type?: unknown } | null)?.type;
    if (!entry || typeof entry !== "object" || Array.isArray(entry) || typeof type !== "string" || !type) {
      throw new LocalCliError({
        code: "ADVISOR_BAD_TOOLS",
        message: `targets.foundry.tools[${index.toString()}] in persona '${persona}' is not a tool object with a 'type'.`,
        hint: `Each entry needs at least \`- type: <foundry tool type>\`. Fix the tools block in personas/${persona}/persona.md.`,
      });
    }
  });
  return rawTools as Record<string, unknown>[];
}

function equalJson(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((entry, i) => equalJson(entry, b[i]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const ar = a as Record<string, unknown>;
    const br = b as Record<string, unknown>;
    const keys = Object.keys(ar);
    return (
      keys.length === Object.keys(br).length &&
      keys.every((key) => Object.prototype.hasOwnProperty.call(br, key) && equalJson(ar[key], br[key]))
    );
  }
  return false;
}

/**
 * Foundry echoes function tools back with `strict: null` added — a field no
 * persona declares and no caller sent. Dropping it is a normalization of the
 * SERVER's round-trip, not a general leniency: `strict: true` / `strict: false`
 * are real, deliberate values and must still compare unequal.
 */
function normalizeFoundryStrictNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeFoundryStrictNulls);
  if (!value || typeof value !== "object") return value;
  const source = value as Record<string, unknown>;
  const normalized = Object.fromEntries(
    Object.entries(source).map(([key, entry]) => [key, normalizeFoundryStrictNulls(entry)]),
  );
  if (source.type === "function" && source.strict === null) delete normalized.strict;
  return normalized;
}

/**
 * Is a tool as DEPLOYED equivalent to the tool as DECLARED by the persona?
 *
 * Key order is irrelevant (JSON objects are unordered); every key and value is
 * otherwise compared by value, because the declaration is forwarded verbatim and
 * any difference is something the deployment did not get.
 */
export function equivalentToolDefinition(a: unknown, b: unknown): boolean {
  return equalJson(normalizeFoundryStrictNulls(a), normalizeFoundryStrictNulls(b));
}

/**
 * How a tool is identified across the declared/deployed boundary. Function tools
 * are keyed by `name`, MCP servers by `server_label`, and everything else by its
 * bare `type` (there is only ever one `web_search_preview`).
 */
export function toolIdentity(tool: unknown): string | null {
  if (!tool || typeof tool !== "object") return null;
  const t = tool as Record<string, unknown>;
  const type = typeof t.type === "string" ? t.type : null;
  if (type === "function") return typeof t.name === "string" ? t.name : null;
  if (type === "mcp") return typeof t.server_label === "string" ? t.server_label : null;
  return type;
}
